import { ChevronRight } from 'lucide-react';
import { WidgetTable } from '@/components/WidgetTable';
import { RiskBadge } from '@/components/RiskBadge';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { useNavigate, useParams } from 'react-router-dom';
import { Fragment, useMemo, useState } from 'react';
import { buildTopAlertDeepLinkUrl } from '@/pages/organization/tabs/DataTableAlertsTab/utils';
import { TopAlertsUserList } from '@/pages/organization/components/WidgetUserLists/TopAlertsUserList';
import { useOrganizationDashboardTopAlerts } from '@/hooks/api/useOrganizationDashboardAlerts';
import { organizationDashboard } from '@kirin/api-client/contracts';
import { formatTopAlertLabel } from '@kirin/api-client/helpers/alerts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  dashboardDefaultTimeRange,
  useDashboardDefaultRangeKey,
} from '../dashboardDefaultRange';

// Local alert model for this widget
export type TopAlert = {
  id: string;
  type: string;
  risk: 'critical' | 'high' | 'medium' | 'low';
  score: number;
  userCount: number;
};

/**
 * Top Alerts Widget: Dashboard widget for top alerts by severity/score,
 * including distinct user counts per alert.
 */
type AlertRow = organizationDashboard.OrganizationDashboardTopAlert;
const LIMIT = 10;
export function TopAlertsWidget() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const [selectedAlert, setSelectedAlert] = useState<AlertRow | null>(null);

  // The last 7 days (the last day for a very busy organization, KIR-5693) in
  // the Settings time zone, re-computed when it changes.
  const timeZone = useDisplayTimeZone();
  const defaultKey = useDashboardDefaultRangeKey();
  const { fromParam, toParam } = useMemo(() => {
    const range = dashboardDefaultTimeRange(defaultKey, timeZone);
    return {
      fromParam: range.from.toISOString(),
      toParam: range.to.toISOString(),
    };
  }, [defaultKey, timeZone]);

  const { isLoading, data, isError, refetch } =
    useOrganizationDashboardTopAlerts(
      organizationId,
      fromParam,
      toParam,
      LIMIT,
      true
    );

  const alerts = data?.alerts || [];

  const handleUsersClick = (alert: AlertRow) => {
    setSelectedAlert(alert);
  };

  const handleCloseModal = () => {
    setSelectedAlert(null);
  };
  const {
    flags,
    isLoading: flagsLoading,
    isError: flagsError,
  } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  // KIR-3809: click an alert row to open the Alerts view pre-filtered to that
  // alert's title + severity (mirrors the Latest Alerts widget's
  // click-through).
  const handleAlertClick = (alert: AlertRow) => {
    const url = buildTopAlertDeepLinkUrl(organizationId, alert, revampEnabled);
    if (url) {
      navigate(url);
    }
  };

  return (
    <Fragment>
      <DashboardWidgetWrapper
        className="col-span-12 xl:col-span-4"
        title="Top Alerts"
        description="Top 10 alerts"
        // Rows stay behind the skeleton until the revamp flag resolves.
        // `useFeatureFlags` reads an unresolved flag as `false`, and every
        // flag-dependent surface here would then be wrong for the org: the
        // header and row labels would render Type and swap to Title, and a
        // click would deep-link to the `t` filter, which revamp-on parks on a
        // hidden column with no header, no icon and nothing to clear — the
        // KIR-3809 regression this widget exists to fix. The alerts query is
        // the slower of the two, so this rarely lengthens the skeleton.
        isLoading={isLoading || flagsLoading}
        isError={isError}
        refetch={refetch}
      >
        <WidgetTable<AlertRow>
          data={alerts}
          className="flex-1 min-h-0"
          getRowId={alert => alert.alertName}
          // A failed flag read also yields `revampEnabled === false`, and the
          // Alerts page resolves the flag independently (its own hook asks for
          // five keys, so `useFeatureFlagsApi` caches it under a different
          // query key). It can therefore read `true` where this widget read
          // `false`, and the click would target the hidden `t` column. The
          // rows themselves stay — a mislabelled row is what every other
          // flag-reading surface degrades to during an outage, and the counts
          // and severities are still worth showing. Only the navigation, whose
          // wrong answer sticks, is withheld; `WidgetTable` drops the pointer
          // cursor and tab stop with it, so the row stops advertising a click
          // it will not honour. The Users drill-down keeps working.
          onRowClick={flagsError ? undefined : handleAlertClick}
          columns={[
            {
              id: 'type',
              header: revampEnabled ? 'Title' : 'Type',
              cell: a => {
                // Must match the Alerts-page cell the deep link lands on:
                // the Title cell with the flag on, the Type cell with it off.
                const label = formatTopAlertLabel(a.alertName, revampEnabled);
                return (
                  <span className="font-medium truncate block" title={label}>
                    {label}
                  </span>
                );
              },
              // KIR-5640: the title takes the room the other columns leave
              // (w-full + max-w-0 lets the cell shrink, so `truncate` cuts at
              // the column edge instead of at a fixed 160px).
              className: 'w-full max-w-0 min-w-[120px]',
            },
            {
              id: 'severity',
              header: 'Severity',
              cell: a => <RiskBadge risk={a.riskLevel} showIcon />,
              className: 'min-w-[100px]',
            },
            {
              // KIR-5640: sized to its content. With the title column taking
              // the free width, a max-width would clip the "Score" header.
              id: 'score',
              header: 'Score',
              cell: a => (
                <span className="text-muted-foreground font-medium tabular-nums">
                  {a.maxScore}
                </span>
              ),
            },
            {
              id: 'users',
              header: 'Users',
              cell: a => (
                <button
                  type="button"
                  onClick={event => {
                    // Don't let the Users drill-down also trigger row
                    // navigation (KIR-3809).
                    event.stopPropagation();
                    handleUsersClick(a);
                  }}
                  // KIR-5640: ml-auto puts the value at the right edge, under
                  // the right-aligned header (a block-level button ignores
                  // text-right and sat ~83px left of it).
                  className="ml-auto flex items-center justify-end gap-1 text-muted-foreground font-medium hover:text-foreground transition-colors"
                >
                  {a.userCount}
                  <ChevronRight
                    className="w-4 h-4 opacity-60"
                    aria-label="View users"
                  />
                </button>
              ),
              className: 'text-right',
            },
          ]}
          emptyState="No Alerts"
        />
      </DashboardWidgetWrapper>
      <TopAlertsUserList
        organizationId={organizationId ?? ''}
        alertName={selectedAlert?.alertName || ''}
        revampEnabled={revampEnabled}
        open={!!selectedAlert}
        onClose={handleCloseModal}
        dateRange={{ from: fromParam, to: toParam }}
      />
    </Fragment>
  );
}
